import React from 'react'
import { graphql } from 'react-apollo'
import { withRouter } from 'react-router'

import { useRemoteStrings } from '@acorns/web-utils'
import { compose, fromRenderProps, withHandlers, withProps } from 'recompose'

import {
  OPTIMIZELY_EXPERIMENT_VARIATION_1,
  OPTIMIZELY_EXPERIMENT_VARIATION_2,
  USER_UUID,
} from 'app-constants'
import { withClickTrack } from 'components/Trackers/ClickTracker'
import { isMobile } from 'components/Trackers/platform'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { ActionType, PageName, TierOption, TierPrice, TierProduct } from 'generated/types'
import SubscribeToTierMutation from 'pages/PricingInterstitial/gql/SubscribeToTier.mutation.gql'
import { TestIDs } from 'test/utils/testIds'
import { withApolloErrorModal } from 'utils/Apollo'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { getVisitedPages } from 'utils/FlowProvider/utils'
import { FunnelContext, FunnelContextProps } from 'utils/FunnelContext'
import removeDuplicateQueryParams from 'utils/sourceAttributes'
import { WithTieredRegistrationProps, withTieredRegistration } from 'utils/TieredRegistration'
import { setTierPriceIdCookie } from 'utils/TieredRegistration/utils'

import { DYNAMIC_COPY, V1_COPY } from './copy'
import {
  Divider,
  TierBodyContainer,
  TierCard,
  TierCardBody,
  TierCardCta,
  TierCardHeader,
  TierCardPrice,
  TierCardTitle,
  TierHeadContainer,
  TierHeadImage,
} from './styled-components'
import { renderProducts } from './TierProduct'
import { renderProducts as renderProductsV1 } from './TierProductV1'
import { renderProducts as renderProductsV2 } from './TierProductV2'

type InnerProps = WithFlowRouterProps &
  WithTieredRegistrationProps & {
    handleTierCta: (tierPriceId: string, tierId: string, isAuthenticated: boolean) => void
    tierKey: string
    tierName: string
    tierDescription: string
    tierPrice: string
    tierPriceId: string
    tierId: string
    tierCta: string
    tierCtaTrackerId: string
    tierProducts: TierProduct[]
    isPreferredTier: boolean
    displayedProductKeys: string[]
    preferredTierText: string
    isAuthenticated: boolean
    optimizelyVariant: string
    productKeys: string[]
  }

type OuterProps = {
  isAuthenticated: boolean
  tierData: TierOption
  preferredTierId: string
  displayedProductKeys: string[]
}

const ctaTrackerIdsByTierKey = {
  bronze: TRACKER_IDS.SELECT_TIER.SELECT_TIER_BRONZE,
  silver: TRACKER_IDS.SELECT_TIER.SELECT_TIER_SILVER,
  gold: TRACKER_IDS.SELECT_TIER.SELECT_TIER_GOLD,
}

function formatTierPrice(preferredTierPrice: TierPrice) {
  const formattedFrequencies = {
    MONTHLY: 'month',
    YEARLY: 'year',
  }

  const tierPriceValue = preferredTierPrice.amount.value
  const tierPriceFrequency = formattedFrequencies[preferredTierPrice.billingFrequency]

  return `$${tierPriceValue}/${tierPriceFrequency}`
}

const enhance = compose<InnerProps, OuterProps>(
  fromRenderProps<Partial<FunnelContextProps>, {}, Partial<FunnelContextProps>>(
    FunnelContext.Consumer,
    ({ hasVisited, isDynamicFunnelEnabled, routeToPage }) => ({
      hasVisited,
      isDynamicFunnelEnabled,
      routeToPage,
    }),
  ),
  withFlowRouter,
  withTieredRegistration,
  withApolloErrorModal,
  withRouter,
  graphql(SubscribeToTierMutation, { name: 'subscribeToTier' }),
  withProps(({ tierData, preferredTierId, isAuthenticated }) => {
    const tierKey = tierData?.tier?.key?.toLowerCase()
    const tierName = tierData?.tier?.name
    const tierDescription = tierData?.tier?.description
    const tierPrice = tierData?.preferredTierPrice
    const tierPriceId = tierData?.preferredTierPrice?.id
    const tierProducts = tierData?.tier?.products

    const tierId = tierData?.tier?.id
    const isPreferredTier = tierId === preferredTierId

    const tierCopy = DYNAMIC_COPY.tiers?.[tierKey]
    const preferredTierText = tierCopy?.preferredTierText
    const tierCta = tierCopy?.cta
    const tierCtaTrackerId = ctaTrackerIdsByTierKey[tierKey]

    return {
      tierKey,
      tierName,
      tierDescription,
      tierCta,
      tierCtaTrackerId,
      isPreferredTier,
      preferredTierText,
      tierPrice,
      tierPriceId,
      tierId,
      tierProducts,
      isAuthenticated,
    }
  }),
  withHandlers({
    handleTierCta: ({
      route,
      location,
      setTierPriceId,
      isDynamicFunnelEnabled,
      routeToPage,
      hasVisited,
      subscribeToTier,
    }) => async (priceID: string, tierID: string, isAuthenticated: boolean) => {
      if (!isAuthenticated) {
        const paramString =
          window.location.search + (window.location.search ? '&' : '?') + `tierPriceId=${priceID}`
        window.location.href = FlowRoute.Signup + paramString
        return
      }
      setTierPriceIdCookie(priceID)
      setTierPriceId(priceID)

      const parsed = removeDuplicateQueryParams(location.search)
      parsed.tierPriceId = priceID

      await subscribeToTier({
        variables: {
          input: {
            tierPriceId: priceID,
            tierId: tierID,
            userId: window.sessionStorage.getItem(USER_UUID),
          },
        },
      })

      const visited = getVisitedPages()
      if (
        visited.includes(FlowRoute.PricingInterstitial) ||
        hasVisited(PageName.PricingInterstitial)
      ) {
        route({
          from: FlowRoute.SelectTier,
          action: 'invest_continue',
          queryParams: parsed,
        })
      } else {
        isDynamicFunnelEnabled
          ? routeToPage(PageName.SelectTier, ActionType.Continue, {
              params: location?.state?.params || window.location.search || '',
            })
          : route({
              from: FlowRoute.SelectTier,
              action: 'continue',
              locationState: {
                params: location?.state?.params || window.location.search || '',
              },
              queryParams: parsed,
            })
      }
    },
  }),
)

export default enhance(
  ({
    tierKey,
    tierName,
    tierDescription,
    tierPrice,
    tierPriceId,
    tierId,
    tierCta,
    tierCtaTrackerId,
    tierProducts,
    isAuthenticated,
    handleTierCta,
    optimizelyVariant,
    productKeys,
  }: InnerProps) => {
    const { getRemoteString } = useRemoteStrings()
    const SelectTierCta = withClickTrack<any, any>(tierCtaTrackerId)(TierCardCta)
    const tierCtaText =
      optimizelyVariant === OPTIMIZELY_EXPERIMENT_VARIATION_1
        ? getRemoteString({ key: 'registration.tier_selection.cta.primary' })
        : tierCta || 'Choose this plan'

    return (
      <TierCard key={tierKey} variant={optimizelyVariant}>
        <TierHeadContainer>
          {optimizelyVariant === OPTIMIZELY_EXPERIMENT_VARIATION_1 && (
            <TierHeadImage src={V1_COPY[tierKey].image} alt="tier image" />
          )}
          <TierCardHeader data-testid={TestIDs.SELECT_TIER.HEADER} variant={optimizelyVariant}>
            <TierCardTitle data-testid={TestIDs.SELECT_TIER.CARD.TITLE}>{tierName}</TierCardTitle>
            <TierCardPrice data-testid={TestIDs.SELECT_TIER.CARD.PRICE}>
              {formatTierPrice(tierPrice)}
            </TierCardPrice>
            {tierDescription && (
              <TierCardBody data-testid={TestIDs.SELECT_TIER.BODY}>
                {optimizelyVariant === OPTIMIZELY_EXPERIMENT_VARIATION_1
                  ? V1_COPY[tierKey].subTitle
                  : tierDescription}
              </TierCardBody>
            )}
          </TierCardHeader>
        </TierHeadContainer>
        {optimizelyVariant === OPTIMIZELY_EXPERIMENT_VARIATION_1 && <Divider />}
        {optimizelyVariant === OPTIMIZELY_EXPERIMENT_VARIATION_1 ? (
          <TierBodyContainer>{renderProductsV1(V1_COPY[tierKey].groups)}</TierBodyContainer>
        ) : tierProducts.length &&
          optimizelyVariant === OPTIMIZELY_EXPERIMENT_VARIATION_2 &&
          isMobile() ? (
          renderProductsV2(productKeys, tierProducts)
        ) : optimizelyVariant === OPTIMIZELY_EXPERIMENT_VARIATION_1 ? (
          renderProducts(tierProducts)
        ) : (
          renderProducts(tierProducts)
        )}
        <SelectTierCta
          data-testid={`${TestIDs.SELECT_TIER.CARD.CTA}_${tierKey.toUpperCase()}`}
          onPress={() => handleTierCta(tierPriceId, tierId, isAuthenticated)}
        >
          {tierCtaText}
        </SelectTierCta>
      </TierCard>
    )
  },
)
